import { useId, useState } from 'react'
import { cn } from '@/lib/utils'

/**
 * Part-to-whole, as a donut with the total in the middle.
 *
 * ## Why a donut here, when this codebase says not to
 *
 * `styles/index.css` is right that `--chart-*` fails a CVD check on adjacent pairs
 * (worst ΔE 3.1 protan) and right that most charts in this app should encode identity
 * with length and position instead. That advice holds wherever a chart *can* use length.
 * A part-to-whole reading cannot: the question is "what share of this went to that", and
 * share against a whole is what an angle shows and a bar does not. So this form gets a
 * validated palette, chosen by `palette`.
 *
 * ## The two palettes, and picking the right one
 *
 * - `categorical` → `--viz-1…8`. For **identity**: a category, an outlet, a tag. Nothing
 *   about VIOLENCE is greater than FRAUD, so nothing in the colour should say so.
 * - `ordinal` → `--ordinal-1…5`. For **ordered** scales: LOW < MEDIUM < HIGH < CRITICAL,
 *   UNVERIFIED → VERIFIED. Ordered data on a categorical palette is the single most
 *   common chart error there is, and severity is where it does the most damage — a desk
 *   scanning for CRITICAL must see it sit at the end of a ramp, not somewhere in a
 *   rainbow.
 *
 * An ordinal chart is drawn in the caller's order and NEVER re-sorted by size: the order
 * is the meaning. A categorical one ranks by value and folds its tail into "Other".
 *
 * ## What keeps it readable
 *
 * - **Colour never carries the meaning alone.** Every slice is also a legend row with its
 *   name, its value and its share in text. Three light-mode steps sit under 3:1 on white,
 *   and this is the relief that makes them legal.
 * - **Colour follows the entity, not its rank.** Categorical slots are assigned from a
 *   stable sort of the ids, so changing a filter does not repaint the segments that
 *   survive it.
 * - **At most eight hues, never cycled.** The tail folds into one neutral "Other" slice
 *   rather than reusing slot 1 for a ninth entity, which would say two things are the
 *   same thing.
 * - **A 2px surface gap** between segments, so adjacent slices never bleed together.
 * - **An empty state that says why it is empty**, rather than drawing an empty ring.
 *
 * This file began as `features/station-mates/SpendDonut.tsx` and is the one donut in the
 * app. A second one would disagree with this one about a rule above within a release.
 */

/** The fixed categorical slot order. Assign 1, 2, 3… — never cycle past the end. */
const VIZ_SLOTS = [
  'var(--viz-1)', 'var(--viz-2)', 'var(--viz-3)', 'var(--viz-4)',
  'var(--viz-5)', 'var(--viz-6)', 'var(--viz-7)', 'var(--viz-8)',
]
/** The ordered ramp. Five steps, monotone lightness; position in the list IS the rank. */
const ORDINAL_SLOTS = [
  'var(--ordinal-1)', 'var(--ordinal-2)', 'var(--ordinal-3)',
  'var(--ordinal-4)', 'var(--ordinal-5)',
]
const OTHER = 'var(--muted-foreground)'

/** How many named slices before the tail folds into Other. Categorical only. */
const MAX_SLICES = 6

export interface DonutSlice {
  /** Stable identity — the categorical colour follows this, never the row's position. */
  id: string
  label: string
  value: number
}

export function Donut({
  slices,
  total,
  emptyMessage,
  formatValue = (v: number) => Math.round(v).toLocaleString(),
  palette = 'categorical',
  centreLabel = 'Total',
  className,
}: {
  slices: DonutSlice[]
  /** The denominator. Passed in rather than summed, so a partial list still reads honestly. */
  total: number
  /** Says WHY there is nothing, not just that there is nothing. */
  emptyMessage: string
  formatValue?: (value: number) => string
  palette?: 'categorical' | 'ordinal'
  centreLabel?: string
  className?: string
}) {
  const titleId = useId()
  const [hovered, setHovered] = useState<string | null>(null)

  // Only positive groups can be drawn as an angle.
  const positive = slices.filter((s) => s.value > 0)
  const parts = positive.reduce((sum, s) => sum + s.value, 0)
  if (positive.length === 0 || parts <= 0) {
    return <p className={cn('text-erp-sm text-muted-foreground p-4', className)}>{emptyMessage}</p>
  }

  /*
     The denominator is the LARGER of the caller's total and the parts.

     A caller may pass a NET total smaller than the sum of the still-positive parts
     (reversals, withdrawals). Dividing by the net would push the arcs past a full turn
     and wrap them over each other. Taking the larger keeps the ring honest; the caption
     below says which of the two the reader is looking at whenever they differ.
  */
  const denom = Math.max(total, parts)
  const excluded = parts - total

  const ordered = palette === 'ordinal'
    // ORDER IS THE MEANING. Ranking an ordinal ramp by size would put CRITICAL in the
    // middle of the ring whenever it happens to be the second-biggest slice.
    ? positive.map((s, i) => ({ ...s, color: ORDINAL_SLOTS[Math.min(i, ORDINAL_SLOTS.length - 1)]! }))
    : rankedCategorical(positive)

  // Geometry. A 40-radius ring in a 100-box; the 2px gap is subtracted from each arc so
  // the surface shows between segments rather than two fills touching.
  const R = 40
  const C = 2 * Math.PI * R
  const GAP = 2
  let offset = 0
  const arcs = ordered.map((s) => {
    const len = Math.max((s.value / denom) * C - GAP, 0.5)
    const arc = { ...s, len, offset }
    offset += (s.value / denom) * C
    return arc
  })

  const share = (v: number) => {
    const pct = (100 * v) / denom
    return `${pct.toFixed(pct > 0 && pct < 1 ? 1 : 0)}%`
  }

  return (
    <div className={cn('p-4 flex flex-wrap items-center gap-6', className)}>
      <div className="relative shrink-0">
        <svg viewBox="0 0 100 100" className="w-40 h-40 -rotate-90"
          role="img" aria-labelledby={titleId}>
          <title id={titleId}>
            Share: {ordered.map((s) => `${s.label} ${share(s.value)}`).join(', ')}
          </title>
          {/* The track shows the uncovered remainder when the slices do not sum to the
              total — an unassigned balance is information, not a rounding artefact. */}
          <circle cx="50" cy="50" r={R} fill="none" stroke="var(--border)" strokeWidth="14" />
          {arcs.map((a) => (
            <circle key={a.id} cx="50" cy="50" r={R} fill="none"
              stroke={a.color}
              strokeWidth={hovered === a.id ? 17 : 14}
              strokeDasharray={`${a.len} ${C - a.len}`}
              strokeDashoffset={-a.offset}
              className="transition-[stroke-width] duration-150"
              onMouseEnter={() => setHovered(a.id)}
              onMouseLeave={() => setHovered(null)} />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-erp-xs text-muted-foreground">
            {hovered ? (ordered.find((s) => s.id === hovered)?.label ?? centreLabel) : centreLabel}
          </span>
          <span className="text-erp-md font-semibold tabular-nums">
            {formatValue(hovered
              ? (ordered.find((s) => s.id === hovered)?.value ?? total)
              : total)}
          </span>
        </div>
      </div>

      {/*
         The legend IS the relief. Every slice carries its name, value and share in text,
         so a reader who cannot separate two hues still has the whole answer.
      */}
      <ul className="min-w-0 flex-1 space-y-1.5">
        {excluded > 0 && (
          <li className="text-erp-xs text-muted-foreground pb-1">
            Shares are of {formatValue(parts)} across the parts; the centre reads{' '}
            {formatValue(total)} because {formatValue(excluded)} of it has since been
            withdrawn or reversed.
          </li>
        )}
        {ordered.map((s) => (
          <li key={s.id}
            className="flex items-center gap-2 text-erp-sm min-w-0"
            onMouseEnter={() => setHovered(s.id)}
            onMouseLeave={() => setHovered(null)}>
            <span aria-hidden className="w-3 h-3 rounded-sm shrink-0"
              style={{ background: s.color }} />
            <span className="truncate flex-1 min-w-0">{s.label}</span>
            <span className="tabular-nums text-muted-foreground shrink-0">{share(s.value)}</span>
            <span className="tabular-nums font-medium shrink-0 w-28 text-right">
              {formatValue(s.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Rank by value, fold the tail, and colour by a STABLE SORT OF THE IDS rather than by
 * rank — a filter that drops one entity must not hand its colour to another.
 */
function rankedCategorical(positive: DonutSlice[]): Array<DonutSlice & { color: string }> {
  const slotById = new Map(
    [...positive].sort((a, b) => a.id.localeCompare(b.id))
      .map((s, i) => [s.id, i < VIZ_SLOTS.length ? VIZ_SLOTS[i]! : OTHER]))

  const ranked = [...positive].sort((a, b) => b.value - a.value)
  const shown = ranked.slice(0, MAX_SLICES)
  const tail = ranked.slice(MAX_SLICES)
  const tailValue = tail.reduce((sum, s) => sum + s.value, 0)

  return [
    ...shown.map((s) => ({ ...s, color: slotById.get(s.id) ?? OTHER })),
    ...(tailValue > 0
      ? [{ id: '__other__', label: `Other (${tail.length})`, value: tailValue, color: OTHER }]
      : []),
  ]
}
